/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { EuiSkeletonText, EuiEmptyPrompt, EuiFlexGroup, EuiFlexItem, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { CuiRouterLinkButton } from '@modules/cui/RouterButton'
import { loginUrl } from '@modules/auth/urls'
import { portalUrl } from '@modules/utils/rootUrls'
import Feature from '@modules/utils/feature'
import { hasUnexpiredSession } from '@modules/auth/auth'

import { isFeatureActivated } from '@/store'

import HttpErrorPage from '../HttpErrorPage'
import { describeSsoError } from '../../../lib/ssoErrors'

const SsoErrors: React.FunctionComponent = () => {
  const navigate = useNavigate()
  const [params] = useSearchParams()

  const isPortalFeatureEnabled = isFeatureActivated(Feature.cloudPortalEnabled)
  const loggedIn = hasUnexpiredSession()
  const errorCode = params.get('error_code')

  useEffect(() => {
    if (isPortalFeatureEnabled) {
      const portalUrlWithQueryString = `${portalUrl()}?error_code=${errorCode}`
      navigate(portalUrlWithQueryString, { replace: true })
    }
  }, [])

  if (isPortalFeatureEnabled) {
    return <EuiSkeletonText />
  }

  const { title, description } = describeSsoError(errorCode)

  return (
    <HttpErrorPage>
      <EuiEmptyPrompt
        titleSize='m'
        title={
          <h2>
            <FormattedMessage {...title} />
          </h2>
        }
        body={
          <EuiText size='s'>
            <FormattedMessage {...description} />
          </EuiText>
        }
        actions={
          <EuiFlexGroup justifyContent='center' alignItems='center' gutterSize='l'>
            {loggedIn ? (
              <EuiFlexItem grow={false}>
                <div>
                  <CuiRouterLinkButton fill={true} to='/' color='primary'>
                    <FormattedMessage id='sso-errors.home-button' defaultMessage='Home' />
                  </CuiRouterLinkButton>
                </div>
              </EuiFlexItem>
            ) : (
              <EuiFlexItem grow={false}>
                <div>
                  <CuiRouterLinkButton fill={true} to={loginUrl()}>
                    <FormattedMessage
                      id='sso-errors.cloud-login-button'
                      defaultMessage='Log in to Cloud'
                    />
                  </CuiRouterLinkButton>
                </div>
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
        }
      />
    </HttpErrorPage>
  )
}

export default SsoErrors
